/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import styled from "styled-components";
import { Link } from "react-router";
import { ChevronRight, Clock } from "lucide-react";

import IconTile from "@src/components/ui/IconTile";
import { ThemePalette } from "@src/components/Theme";
import {
  HUB_ID,
  sectionPath,
  type Section,
} from "@src/components/modules/NavigationModule/Navigation/sections";
import {
  usedAgo,
  type RecentSection,
} from "@src/components/modules/NavigationModule/Navigation/recentSections";

/*
 * The Overview page's cards, as AWS Management's ConnectionHomePage and the Ops Console's cloud
 * homes have them: the sections this user opened most recently first ("Used 2 days ago"), then
 * every other section. Usage only reorders; nothing is ever hidden.
 */
const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const Block = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Heading = styled.h2`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  font-weight: 600;
  color: ${ThemePalette.grayscale[6]};
  svg {
    color: ${ThemePalette.grayscale[3]};
  }
`;
const Grid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: minmax(0, 1fr); /* was 1fr: long subtitles widened the page on a phone */
  @media (min-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
`;
const Chev = styled.span`
  display: flex;
  color: ${ThemePalette.grayscale[2]};
  flex-shrink: 0;
`;
const Card = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border: 1px solid ${ThemePalette.grayscale[0]};
  border-radius: 8px;
  background: var(--t9-bg-surface);
  color: inherit;
  text-decoration: none;
  transition:
    border-color 150ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 150ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover {
    border-color: var(--t9-brand-300); /* --t9-brand-300 */
    box-shadow: 0 2px 8px rgba(44, 62, 80, 0.08);
  }
  &:hover ${Chev} {
    color: ${ThemePalette.grayscale[3]};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
const CardBody = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;
const CardTitle = styled.span`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${ThemePalette.black};
`;
const CardSub = styled.span`
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SectionCard = ({ section, subtitle }: { section: Section; subtitle: string }) => (
  <Card to={sectionPath(section)} aria-label={`${section.label}: ${subtitle}`}>
    <IconTile icon={section.icon} tone={section.tone} size={36} />
    <CardBody>
      <CardTitle>{section.label}</CardTitle>
      <CardSub title={subtitle}>{subtitle}</CardSub>
    </CardBody>
    <Chev aria-hidden="true">
      <ChevronRight size={16} strokeWidth={2} />
    </Chev>
  </Card>
);

type Props = {
  sections: Section[];
  recent: Record<string, RecentSection>;
  now?: number;
};

const HubSections = ({ sections, recent, now }: Props) => {
  const cards = sections.filter(s => s.id !== HUB_ID);
  const used = cards
    .filter(s => recent[s.id])
    .sort((a, b) => recent[b.id].lastVisitedAt.localeCompare(recent[a.id].lastVisitedAt));
  const usedIds = new Set(used.map(s => s.id));
  const rest = cards.filter(s => !usedIds.has(s.id));

  return (
    <Stack>
      {used.length ? (
        <Block aria-labelledby="hub-recent">
          <Heading id="hub-recent">
            <Clock size={14} strokeWidth={2} aria-hidden="true" /> Recently used
          </Heading>
          <Grid>
            {used.map(s => (
              <SectionCard
                key={s.id}
                section={s}
                subtitle={`Used ${usedAgo(recent[s.id].lastVisitedAt, now)}`}
              />
            ))}
          </Grid>
        </Block>
      ) : null}
      {rest.length ? (
        <Block aria-labelledby="hub-all">
          <Heading id="hub-all">{used.length ? "All sections" : "Sections"}</Heading>
          <Grid>
            {rest.map(s => (
              <SectionCard key={s.id} section={s} subtitle={s.subtitle} />
            ))}
          </Grid>
        </Block>
      ) : null}
    </Stack>
  );
};

export default HubSections;
